import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import service from '@/utils/fetch';
import { checkPushPermissions, requestAllPushPermissions } from '@/utils/pushPermission';
import { handlePushNotificationOpen } from '@/utils/pushNavigation';

let pushNavigationRouter = null;

/** 由 main.js 注入，用于点击通知后路由跳转 */
export function setPushNavigationRouter(router) {
  pushNavigationRouter = router;
}

let initialized = false;
let badgeListenersBound = false;
let fcmToken = null; // 保存 FCM token
let jpushRegistrationId = null; // 保存 JPush regId
let isHuaweiDevice = false; // 是否为华为设备
let fcmListenersBound = false;
let jpushListenersBound = false;
let resumeListenersBound = false;
let pushBindTimer = null;
let lastUploadedRegId = null;
let ensureBindInFlight = null;
let lastEnsureBindAt = 0;
const ENSURE_BIND_DEBOUNCE_MS = 4000;
const FORCE_BIND_REASONS = new Set([
  'onUserLoggedIn',
  'privacy-agree',
  'main-bootstrap',
  'registration-event',
  'initPush-end',
]);

function toBooleanFlag(value) {
  if (value === true || value === 1 || value === '1' || value === 'true') return true;
  if (value === false || value === 0 || value === '0' || value === 'false') return false;
  return value;
}

function safeStringify(value) {
  try {
    return JSON.stringify(value);
  } catch (e) {
    return `[unserializable:${e.message || e}]`;
  }
}

function logPushObject(label, payload) {
  console.log(`${label} ${safeStringify(payload)}`);
}

function readJsonStorage(storage, key) {
  try {
    if (!storage) return null;
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.warn('[Push] Failed to parse storage json', { key, message: e.message || e });
    return null;
  }
}

function extractJPushRegistrationId(event) {
  if (!event) return null;
  return event.registrationId || event.registrationID || event.regId || event.detail?.registrationId || null;
}

function bindJPushDocumentListeners() {
  if (jpushListenersBound || typeof document === 'undefined') {
    return;
  }

  jpushListenersBound = true;

  document.addEventListener('jpush.receiveRegistrationId', async (event) => {
    try {
      console.log('[Push] jpush.receiveRegistrationId event:', event);
      const id = extractJPushRegistrationId(event);
      console.log('[Push] parsed reg id from event:', maskToken(id));
      if (id) {
        jpushRegistrationId = id;
        console.log('[Push] regId from jpush.receiveRegistrationId event');
        await tryUploadJPushRegId('registration-event', { force: true, maxAttempts: 3 });
      } else {
        console.warn('[Push] JPush receiveRegistrationId event without id:', event);
      }
    } catch (e) {
      console.error('[Push] Error handling jpush.receiveRegistrationId event:', e);
    }
  }, false);

  document.addEventListener('jpush.receiveNotification', (event) => {
    console.log('[Push] JPush receiveNotification:', event);
    logJPushProbe('event-receiveNotification', {
      notificationId: event?.['cn.jpush.android.NOTIFICATION_ID'] || event?.notificationId,
      title: event?.title,
      alert: event?.alert,
      extras: event?.['cn.jpush.android.EXTRA'] || event?.extra,
    });
  }, false);

  document.addEventListener('jpush.openNotification', (event) => {
    console.log('[Push] JPush openNotification:', event);
    const cached = typeof window !== 'undefined' ? (window.JPush?.openNotification || window.plugins?.jPushPlugin?.openNotification) : null;
    if (cached) {
      console.log(`[Push] JPush openNotification cached ${JSON.stringify({
        keys: Object.keys(cached),
        extra: cached['cn.jpush.android.EXTRA'] ?? cached.extras ?? null,
      })}`);
    }
    logJPushProbe('event-openNotification', {
      notificationId: event?.['cn.jpush.android.NOTIFICATION_ID'] || event?.notificationId,
      title: event?.title,
      alert: event?.alert,
      extras: event?.['cn.jpush.android.EXTRA'] || event?.extras || event?.extra,
      cachedExtra: cached?.['cn.jpush.android.EXTRA'] || cached?.extras || null,
    });
    clearPushBadge();
    handlePushNotificationOpen(event, pushNavigationRouter).catch((e) => {
      console.warn('[Push] handlePushNotificationOpen failed:', e);
    });
  }, false);

  document.addEventListener('jpush.receiveMessage', (event) => {
    console.log('[Push] JPush receiveMessage:', event);
    logJPushProbe('event-receiveMessage', {
      messageId: event?.['cn.jpush.android.MSG_ID'] || event?.messageId,
      title: event?.title,
      alert: event?.alert || event?.message,
      extras: event?.['cn.jpush.android.EXTRA'] || event?.extra,
    });
  }, false);

  console.log('[Push] JPush document listeners bound');
}

function logJPushProbe(stage, extra = {}) {
  logPushObject('[Push] JPush probe', {
    stage,
    ...getPushDebugContext(),
    ...extra,
  });
}

function callJPushWithSingleCallback(JPush, methodName, stage, timeoutMs = 5000) {
  return new Promise((resolve) => {
    if (!JPush || typeof JPush[methodName] !== 'function') {
      logJPushProbe(stage, { methodName, available: false });
      resolve(null);
      return;
    }

    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      logJPushProbe(stage, { methodName, result: 'timeout', timeoutMs });
      resolve(null);
    }, timeoutMs);

    try {
      JPush[methodName]((value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        logJPushProbe(stage, {
          methodName,
          result: 'received',
          value,
        });
        resolve(value);
      });
    } catch (e) {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      console.warn('[Push] JPush callback method threw error', {
        methodName,
        stage,
        message: e.message || e,
      });
      resolve(null);
    }
  });
}

async function requestJPushRegistrationId(JPush, stage, timeoutMs = 5000) {
  return new Promise((resolve) => {
    if (!JPush || typeof JPush.getRegistrationID !== 'function') {
      logJPushProbe(stage, { available: false });
      resolve(null);
      return;
    }

    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      logJPushProbe(stage, { timeoutMs, result: 'timeout' });
      resolve(null);
    }, timeoutMs);

    try {
      JPush.getRegistrationID((id) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        logJPushProbe(stage, {
          result: id ? 'received' : 'empty',
          registrationId: maskToken(id),
        });
        resolve(id || null);
      });
    } catch (e) {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      console.error('[Push] JPush getRegistrationID threw error', { stage, message: e.message || e });
      resolve(null);
    }
  });
}

async function runJPushDiagnostics(JPush, stage) {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const diagnostics = {
    stage,
    platform: Capacitor.getPlatform(),
    ...getPushDebugContext(),
  };

  const registrationId = await requestJPushRegistrationId(JPush, `${stage}-registration-id`, 3500);
  if (registrationId) {
    diagnostics.registrationId = maskToken(registrationId);
    jpushRegistrationId = registrationId;
  }

  const pushStopped = await callJPushWithSingleCallback(JPush, 'isPushStopped', `${stage}-isPushStopped`, 2500);
  if (pushStopped !== null) {
    diagnostics.isPushStopped = toBooleanFlag(pushStopped);
  }

  const notificationsEnabled = await callJPushWithSingleCallback(
    JPush,
    'getUserNotificationSettings',
    `${stage}-notification-settings`,
    2500
  );
  if (notificationsEnabled !== null) {
    diagnostics.notificationsEnabled = toBooleanFlag(notificationsEnabled);
  }

  const connectionState = await callJPushWithSingleCallback(
    JPush,
    'getConnectionState',
    `${stage}-connection-state`,
    2500
  );
  if (connectionState !== null) {
    diagnostics.connectionState = toBooleanFlag(connectionState);
  }

  logPushObject('[Push] JPush diagnostics', diagnostics);
}

function maskToken(token) {
  if (!token || typeof token !== 'string') return token;
  if (token.length <= 12) return token;
  return `${token.slice(0, 6)}...${token.slice(-6)}`;
}

function getPushDebugContext() {
  return {
    native: Capacitor.isNativePlatform(),
    platform: Capacitor.getPlatform(),
    agreed: hasPrivacyAgreement(),
    loggedIn: isUserLoggedIn(),
    hasJPushPlugin: typeof window !== 'undefined' && !!window.JPush,
    hasJCorePlugin: typeof window !== 'undefined' && !!window.JCore,
    jpushRegistrationId: maskToken(jpushRegistrationId),
    fcmToken: maskToken(fcmToken),
  };
}

function isIOSNative() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

function isIOSJPushOnlyMode() {
  return isIOSNative();
}

function getPushDeviceType() {
  return isIOSNative() ? 'ios' : 'android';
}

/**
 * 获取推送调试信息
 */
export function getPushDebugInfo() {
  const info = {
    platform: Capacitor.getPlatform(),
    isXiaomi: isXiaomiDevice(),
    jpushRegistrationId: maskToken(jpushRegistrationId),
    fcmToken: maskToken(fcmToken),
    hasJPushPlugin: typeof window !== 'undefined' && !!window.JPush,
    hasJCorePlugin: typeof window !== 'undefined' && !!window.JCore,
  };
  
  console.log('[Push] Debug Info:', info);
  return info;
}

/**
 * 检测是否为小米设备
 */
function isXiaomiDevice() {
  try {
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      const ua = navigator.userAgent.toLowerCase();
      // 检测小米设备标识
      return /xiaomi|mi\s|redmi|miui/.test(ua);
    }
  } catch (e) {
    console.warn('[Push] Error detecting Xiaomi device:', e);
  }
  return false;
}

/**
 * 检查是否需要显示小米推送引导
 * @returns {boolean}
 */
export function shouldShowXiaomiPushGuide() {
  if (!isXiaomiDevice()) {
    return false;
  }

  try {
    // 检查是否已经引导过
    const hasGuided = localStorage.getItem('xiaomi_push_guided');
    if (hasGuided === 'true') {
      return false;
    }

    // 检查用户是否取消过，如果取消过且未超过7天，不再显示
    const dismissedTime = localStorage.getItem('xiaomi_push_guide_dismissed');
    if (dismissedTime) {
      const daysPassed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      if (daysPassed < 7) {
        return false;
      }
    }

    return true;
  } catch (e) {
    console.warn('[Push] Error checking xiaomi guide status:', e);
    return false;
  }
}

/**
 * 检查用户是否已同意隐私政策
 * 只有在用户明确同意后，才允许初始化推送 SDK（JPush / FCM）
 */
function hasPrivacyAgreement() {
  try {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    const agreed = localStorage.getItem('privacyAgreementAgreed');
    return agreed === 'true';
  } catch (e) {
    console.warn('[Push] 检查隐私协议状态失败，将视为未同意:', e);
    return false;
  }
}

/**
 * 同步 JCore/JPush 合规开关到原生侧（SharedPreferences + JCollectionAuth）
 * 目的：
 * - 老用户可能已在 H5 localStorage 同意，但原生侧还没记录（首次升级后需要补写）
 * - 确保在 init JPush/FCM 之前，原生侧已经被设置为“已同意”
 */
export function syncNativePrivacyConsent(agreed) {
  try {
    const val = !!agreed;
    // JCore: 持久化到 xpz_privacy，并控制采集/自唤醒（小米后台推送关键）
    if (typeof window !== 'undefined' && window.JCore) {
      if (typeof window.JCore.setAuth === 'function') {
        window.JCore.setAuth(val);
      }
      if (val && typeof window.JCore.enableAutoWakeup === 'function') {
        window.JCore.enableAutoWakeup(true);
      } else if (!val && typeof window.JCore.enableAutoWakeup === 'function') {
        window.JCore.enableAutoWakeup(false);
      }
    }
    // 部分版本 JPush 也暴露 setAuth，保持兼容
    if (typeof window !== 'undefined' && window.JPush && typeof window.JPush.setAuth === 'function') {
      window.JPush.setAuth(val);
    }
  } catch (e) {
    console.warn('[Push] syncNativePrivacyConsent failed:', e);
  }
}

/**
 * 检测是否为华为设备
 */
function detectHuaweiDevice() {
  try {
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      const ua = navigator.userAgent.toLowerCase();
      // 检测华为设备标识
      return /huawei|honor|hmos/.test(ua) || 
             /huawei/i.test(navigator.userAgent) ||
             /honor/i.test(navigator.userAgent);
    }
  } catch (e) {
    console.warn('[Push] Error detecting Huawei device:', e);
  }
  return false;
}

/**
 * 检查用户是否已登录
 */
function isUserLoggedIn() {
  try {
    const sessionUser = readJsonStorage(typeof sessionStorage !== 'undefined' ? sessionStorage : null, 'userInfoXpz');
    if (sessionUser?.access_token) return true;

    const localUser = readJsonStorage(typeof localStorage !== 'undefined' ? localStorage : null, 'userInfoXpz');
    if (localUser?.access_token) return true;

    const authData = readJsonStorage(typeof localStorage !== 'undefined' ? localStorage : null, 'xpzAuthData');
    if (authData?.accessToken) return true;

    if (typeof localStorage !== 'undefined' && localStorage.getItem('token')) return true;
  } catch (e) {
    console.error('[Push] Error checking login status:', e);
  }
  return false;
}

function getJPushPlugin() {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.JPush || null;
}

/**
 * Cordova JPush 插件晚于 Vue 启动，首装时 window.JPush 可能尚未注入
 */
function waitForJPushPlugin(timeoutMs = 20000) {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const check = () => {
      const plugin = getJPushPlugin();
      if (plugin) {
        resolve(plugin);
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        resolve(null);
        return;
      }
      setTimeout(check, 250);
    };

    if (typeof document !== 'undefined') {
      const onDeviceReady = () => {
        document.removeEventListener('deviceready', onDeviceReady);
        check();
      };
      document.addEventListener('deviceready', onDeviceReady, false);
    }
    check();
  });
}

function readLastUploadedRegId() {
  try {
    return localStorage.getItem('jpush_regid_last_uploaded') || null;
  } catch (e) {
    return null;
  }
}

function markRegIdUploaded(regId) {
  lastUploadedRegId = regId;
  try {
    localStorage.setItem('jpush_regid_last_uploaded', regId);
    localStorage.setItem('jpush_regid_uploaded_at', Date.now().toString());
  } catch (e) {
    // ignore
  }
}

/**
 * 获取 regId 并上报服务端（厂商通道在 regId 绑定后才真正生效）
 */
async function tryUploadJPushRegId(reason, options = {}) {
  const { force = false, maxAttempts = 10 } = options;

  if (!hasPrivacyAgreement() || !Capacitor.isNativePlatform()) {
    return false;
  }

  let JPush = getJPushPlugin();
  if (!JPush) {
    JPush = await waitForJPushPlugin(12000);
  }
  if (!JPush) {
    console.warn('[Push] tryUploadJPushRegId: JPush plugin still not ready', { reason });
    return false;
  }

  bindJPushDocumentListeners();

  if (typeof JPush.init === 'function') {
    try {
      JPush.init();
    } catch (e) {
      console.warn('[Push] JPush.init in tryUpload failed', e);
    }
  }
  JPush.setDebugMode && JPush.setDebugMode(true);

  let regId = jpushRegistrationId;
  if (!regId) {
    regId = await fetchJPushRegistrationIdWithRetry(JPush, `upload-${reason}`, maxAttempts);
  }
  if (!regId) {
    console.warn('[Push] tryUploadJPushRegId: regId empty', { reason });
    return false;
  }

  jpushRegistrationId = regId;

  if (!isUserLoggedIn()) {
    console.log('[Push] regId ready, wait for login before upload', { reason, regId: maskToken(regId) });
    return false;
  }

  if (!lastUploadedRegId) {
    lastUploadedRegId = readLastUploadedRegId();
  }
  if (!force && lastUploadedRegId && lastUploadedRegId === regId) {
    console.log('[Push] regId already uploaded, skip', { reason, regId: maskToken(regId) });
    return true;
  }

  try {
    await saveTokenToServer(regId, 'jpush', { forceLoggedIn: true });
    markRegIdUploaded(regId);
    logPushObject('[Push] regId bound to server', { reason, regId: maskToken(regId) });
    return true;
  } catch (e) {
    console.warn('[Push] tryUploadJPushRegId save failed', { reason, message: e.message || e });
    return false;
  }
}

function scheduleDeferredPushBind(reason, maxRounds = 25) {
  if (pushBindTimer) {
    clearTimeout(pushBindTimer);
    pushBindTimer = null;
  }

  let round = 0;
  const tick = async () => {
    round += 1;
    if (!hasPrivacyAgreement() || !Capacitor.isNativePlatform() || !isUserLoggedIn()) {
      return;
    }

    const ok = await tryUploadJPushRegId(`deferred-${reason}-r${round}`, {
      force: round <= 2,
      maxAttempts: 6,
    });

    if (ok) {
      console.log('[Push] deferred bind succeeded', { reason, round });
      return;
    }

    if (round < maxRounds) {
      pushBindTimer = setTimeout(tick, 3000);
    } else {
      console.warn('[Push] deferred bind gave up', { reason, maxRounds });
    }
  };

  pushBindTimer = setTimeout(tick, 2000);
}

/**
 * 确保 JPush regId 已上报（解决首装停留在主页收不到站外推送的问题）
 */
export async function ensurePushTokenBound(reason = 'manual', options = {}) {
  if (!hasPrivacyAgreement() || !Capacitor.isNativePlatform()) {
    return false;
  }

  const forceUpload = options.force === true || FORCE_BIND_REASONS.has(reason);
  const now = Date.now();
  if (!forceUpload && now - lastEnsureBindAt < ENSURE_BIND_DEBOUNCE_MS) {
    if (lastUploadedRegId || readLastUploadedRegId()) {
      return true;
    }
  }

  if (ensureBindInFlight) {
    return ensureBindInFlight;
  }

  ensureBindInFlight = (async () => {
    logPushObject('[Push] ensurePushTokenBound', { reason, forceUpload, ...getPushDebugContext() });

    if (!initialized) {
      await initPush();
    }

    syncNativePrivacyConsent(true);

    const ok = await tryUploadJPushRegId(reason, {
      force: forceUpload,
      maxAttempts: forceUpload ? 12 : 6,
    });
    lastEnsureBindAt = Date.now();
    if (!ok && isUserLoggedIn()) {
      scheduleDeferredPushBind(reason);
    }
    return ok;
  })();

  try {
    return await ensureBindInFlight;
  } finally {
    ensureBindInFlight = null;
  }
}

export function clearPushBadge() {
  try {
    if (!Capacitor.isNativePlatform()) return;

    const JPush = getJPushPlugin();
    if (!JPush) return;

    if (typeof JPush.setBadgeNumber === 'function') {
      JPush.setBadgeNumber(0);
    }
    if (typeof JPush.clearAllNotification === 'function') {
      JPush.clearAllNotification();
    }
    if (typeof JPush.setApplicationIconBadgeNumber === 'function') {
      JPush.setApplicationIconBadgeNumber(0);
    }
    if (typeof JPush.resetBadge === 'function') {
      JPush.resetBadge();
    }
  } catch (e) {
    console.warn('[Push] clearPushBadge failed:', e);
  }
}

function bindBadgeClearListeners() {
  if (badgeListenersBound || typeof document === 'undefined') {
    return;
  }
  badgeListenersBound = true;

  document.addEventListener('resume', () => {
    clearPushBadge();
  }, false);

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      clearPushBadge();
    }
  });
}

async function refreshJPushOnResume() {
  if (!Capacitor.isNativePlatform() || !hasPrivacyAgreement()) {
    return;
  }

  logJPushProbe('resume-refresh');
  await ensurePushTokenBound('resume');

  const JPush = getJPushPlugin();
  if (JPush) {
    await runJPushDiagnostics(JPush, 'resume');
  }
}

function bindResumePushListeners() {
  if (resumeListenersBound || typeof document === 'undefined') {
    return;
  }
  resumeListenersBound = true;

  document.addEventListener('resume', () => {
    refreshJPushOnResume().catch((e) => {
      console.warn('[Push] refreshJPushOnResume failed:', e);
    });
  }, false);

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      refreshJPushOnResume().catch((e) => {
        console.warn('[Push] refreshJPushOnResume failed:', e);
      });
    }
  });
}

/**
 * 登录后完善 Android 推送权限（小米需自启动/省电豁免，否则后台收不到）
 */
export async function setupAndroidPushAfterLogin() {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
    return;
  }

  try {
    const status = await checkPushPermissions();
    console.log('[Push] setupAndroidPushAfterLogin status:', status);

    if (!status.notificationPermission) {
      await requestAllPushPermissions({ showBatteryOptimization: false });
    }

    const { checkAndShowVendorPushGuideAfterLogin } = await import('@/utils/vendorPushGuide');
    await checkAndShowVendorPushGuideAfterLogin();

    const { isXiaomiDevice } = await import('@/utils/xiaomiPermissionGuide');
    const { isHuaweiDevice } = await import('@/utils/huaweiPermissionGuide');
    const isOemNeedingBattery = (await isXiaomiDevice()) || (await isHuaweiDevice());

    if (isOemNeedingBattery && !status.batteryOptimization) {
      setTimeout(async () => {
        try {
          const { requestBatteryOptimization } = await import('@/utils/pushPermission');
          await requestBatteryOptimization();
        } catch (e) {
          console.warn('[Push] requestBatteryOptimization failed:', e);
        }
      }, 3500);
    } else if (!status.batteryOptimization) {
      const { requestBatteryOptimization } = await import('@/utils/pushPermission');
      await requestBatteryOptimization();
    }
  } catch (e) {
    console.warn('[Push] setupAndroidPushAfterLogin failed:', e);
  }
}

/**
 * 保存 token 到服务器
 * @param {string} token - 设备推送 token
 * @param {'fcm'|'jpush'} type - 推送通道类型
 */
export async function saveTokenToServer(token, type = 'fcm', options = {}) {
  const { forceLoggedIn = false } = options;
  if (!token) {
    console.warn('[Push] No token provided to save', { type });
    return;
  }

  if (isIOSJPushOnlyMode() && type !== 'jpush') {
    console.log('[Push] iOS JPush-only mode, skip non-JPush token upload', {
      type,
      token: maskToken(token),
    });
    return;
  }

  // 检查用户是否已登录
  if (!forceLoggedIn && !isUserLoggedIn()) {
    console.log('[Push] User not logged in, token will be saved after login', {
      type,
      token: maskToken(token),
    });
    return;
  }

  try {
    const baseURL = service.defaults.baseURL || 'https://xpzhp.com';
    const requestPath = '/api/account/jpush/save-token';
    const device = getPushDeviceType();
    const fullUrl = `${requestPath}?fcmToken=${encodeURIComponent(token)}&type=${encodeURIComponent(type)}&device=${encodeURIComponent(device)}`;
    
    logPushObject('[Push] Saving token to server', {
      device,
      type,
      token: maskToken(token),
      url: baseURL + fullUrl,
    });
    
    const response = await service.post(fullUrl, null, {
      noLoading: true,
    });
    logPushObject('[Push] Save token success', {
      device,
      type,
      token: maskToken(token),
      status: response?.status,
      msg: response?.msg,
      ret: response?.ret,
    });
    return response;
  } catch (e) {
    console.error('[Push] Save token failed', {
      device: getPushDeviceType(),
      type,
      token: maskToken(token),
      message: e.message || 'Unknown error',
    });
    
    if (e.response) {
      console.error('[Push] response status:', e.response.status);
      console.error('[Push] response statusText:', e.response.statusText);
      console.error('[Push] response data:', e.response.data);
    } else {
      console.error('[Push] No response object (可能是网络错误或CORS问题)');
    }
    
    if (e.config) {
      console.error('[Push] request URL:', e.config.baseURL + e.config.url);
      console.error('[Push] request method:', e.config.method);
    }
    
    throw e;
  }
}

/**
 * 初始化 JPush（厂商通道）
 */
async function initJPush() {
  // 未同意隐私协议时，不初始化 JPush，避免通过 SDK 获取任何用户信息
  if (!hasPrivacyAgreement()) {
    console.log('[Push] 用户未同意隐私协议，跳过 JPush 初始化');
    return;
  }

  if (!Capacitor.isNativePlatform()) return;

  const JPush = await waitForJPushPlugin(20000);
  if (!JPush) {
    console.warn('[Push] JPush plugin not ready during initJPush, will retry via ensurePushTokenBound', getPushDebugContext());
    if (isUserLoggedIn()) {
      scheduleDeferredPushBind('initJPush-no-plugin');
    }
    return;
  }

  try {
    logPushObject('[Push] initJPush start', {
      ...getPushDebugContext(),
      jpushObjectType: typeof JPush,
    });
    if (typeof JPush.init === 'function') {
      JPush.init();
    } else if (isIOSNative()) {
      console.warn('[Push] iOS platform but JPush.init is unavailable');
    } else {
      console.warn('[Push] Android JPush.init is unavailable');
    }

    if (isIOSNative()) {
      console.log('[Push] iOS detected, native AppDelegate bridge handles JPush startup');
    }
    JPush.setDebugMode && JPush.setDebugMode(true); // 调试可开
    bindJPushDocumentListeners();
    logJPushProbe('init-before-fetch');
    await runJPushDiagnostics(JPush, 'init-before-fetch');

    // 获取注册 ID（立即尝试一次）
    const fetchRegId = async (attempt = 1) => {
      try {
        logJPushProbe('fetchRegId-attempt', { attempt });
        const id = await requestJPushRegistrationId(JPush, `init-attempt-${attempt}`, 4000 + attempt * 500);
        if (id) {
          logPushObject('[Push] JPush regId received', { id: maskToken(id), attempt });
          jpushRegistrationId = id;
          await runJPushDiagnostics(JPush, `fetchRegId-success-${attempt}`);
          await tryUploadJPushRegId(`init-fetch-${attempt}`, { maxAttempts: 2 });
        } else {
          console.warn('[Push] JPush regId is empty', { attempt });
          if (attempt < 5) {
            setTimeout(() => {
              fetchRegId(attempt + 1).catch((e) => {
                console.error('[Push] fetchRegId retry error:', e);
              });
            }, 1500 * attempt);
          }
        }
      } catch (e) {
        console.error('[Push] fetchRegId error:', e);
      }
    };
    await fetchRegId();

  } catch (err) {
    console.error('[Push] JPush init failed:', err);
  }
}

/**
 * 初始化 FCM（GMS 兜底）
 */
async function initFCM() {
  if (isIOSJPushOnlyMode()) {
    console.log('[Push] iOS JPush-only mode, skip FCM initialization');
    return;
  }

  // 未同意隐私协议时，不初始化 FCM
  if (!hasPrivacyAgreement()) {
    console.log('[Push] 用户未同意隐私协议，跳过 FCM 初始化');
    return;
  }

  // 只在原生环境运行，避免 Web/PC 报错
  if (!Capacitor.isNativePlatform()) return;

  // 检测是否为华为设备
  isHuaweiDevice = detectHuaweiDevice();
  if (isHuaweiDevice) {
    console.log('[Push] 检测到华为设备，FCM 推送可能不可用（需要 Google Play Services）');
    console.log('[Push] 如需更好支持，依赖厂商通道/JPush');
  }

  console.log('[Push] initFCM start', {
    ...getPushDebugContext(),
    isHuaweiDevice,
  });

  if (!fcmListenersBound) {
    fcmListenersBound = true;

    // 监听设备 token 注册成功
    PushNotifications.addListener('registration', async ({ value }) => {
      console.log('[Push] FCM token received', { token: maskToken(value) });
      fcmToken = value;

      // 如果用户已登录，立即保存 token
      if (isUserLoggedIn()) {
        console.log('[Push] User already logged in, saving FCM token...');
        await saveTokenToServer(value, 'fcm');
      } else {
        console.log('[Push] User not logged in, FCM token saved locally, will save after login');
      }
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('[Push] registrationError raw:', err);
      try {
        console.error(
          '[Push] registrationError json:',
          JSON.stringify(err, Object.getOwnPropertyNames(err || {}), 2)
        );
      } catch (e) {
        console.error('[Push] registrationError stringify failed:', e);
      }

      if (err?.message) console.error('[Push] registrationError message:', err.message);
      if (err?.error) console.error('[Push] registrationError error:', err.error);
      if (err?.code) console.error('[Push] registrationError code:', err.code);

      if (isHuaweiDevice) {
        console.warn('[Push] 华为设备 FCM 注册失败，这是正常的（无 GMS）');
      }
    });

    PushNotifications.addListener('pushNotificationReceived', (notif) => {
      console.log('[Push] pushNotificationReceived', notif);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (notif) => {
      console.log('[Push] pushNotificationActionPerformed', notif);
    });

    console.log('[Push] FCM listeners bound');
  }

  const perm = await PushNotifications.requestPermissions();
  console.log('[Push] Push permission result', perm);
  if (perm.receive !== 'granted') {
    console.log('[Push] Permission not granted');
    return;
  }

  // 设置超时，避免在无 GMS 设备无限等待
  const registerPromise = PushNotifications.register();
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      if (isHuaweiDevice) {
        console.warn('[Push] FCM 注册超时，可能是华为设备无 GMS');
      }
      reject(new Error('Push registration timeout'));
    }, 10000); // 10秒超时
  });

  try {
    await Promise.race([registerPromise, timeoutPromise]);
  } catch (err) {
    if (err.message === 'Push registration timeout') {
      console.warn('[Push] FCM 注册超时，跳过 FCM 初始化');
      return;
    }
    throw err;
  }

  // 监听设备 token 注册成功
  PushNotifications.addListener('registration', async ({ value }) => {
    console.log('[Push] FCM token:', value);
    fcmToken = value;

    // 如果用户已登录，立即保存 token
    if (isUserLoggedIn()) {
      console.log('[Push] User already logged in, saving FCM token...');
      await saveTokenToServer(value, 'fcm');
    } else {
      console.log('[Push] User not logged in, FCM token saved locally, will save after login');
    }
  });

  PushNotifications.addListener('registrationError', (err) => {
    console.error('[Push] registrationError raw:', err);
    try {
      console.error(
        '[Push] registrationError json:',
        JSON.stringify(err, Object.getOwnPropertyNames(err || {}), 2)
      );
    } catch (e) {
      console.error('[Push] registrationError stringify failed:', e);
    }

    if (err?.message) console.error('[Push] registrationError message:', err.message);
    if (err?.error) console.error('[Push] registrationError error:', err.error);
    if (err?.code) console.error('[Push] registrationError code:', err.code);

    if (isHuaweiDevice) {
      console.warn('[Push] 华为设备 FCM 注册失败，这是正常的（无 GMS）');
    }
  });

  PushNotifications.addListener('pushNotificationReceived', (notif) => {
    console.log('[Push] received', notif);
    clearPushBadge();
  });

  PushNotifications.addListener('pushNotificationActionPerformed', (notif) => {
    console.log('[Push] action', notif);
    clearPushBadge();
  });
}

/**
 * 初始化推送服务（应用启动时调用）
 * 优先 JPush（厂商通道），FCM 作为兜底
 */
export async function initPush() {
  if (initialized) {
    console.log('[Push] initPush skipped because already initialized', getPushDebugContext());
    return;
  }
  // 未同意隐私协议时，整个推送体系不初始化
  if (!hasPrivacyAgreement()) {
    console.log('[Push] 用户未同意隐私协议，跳过推送服务初始化');
    return;
  }
  if (!Capacitor.isNativePlatform()) return;
  console.log('[Push] initPush start', getPushDebugContext());
  
  // Android平台：先检查和请求推送权限
  if (Capacitor.getPlatform() === 'android') {
    try {
      console.log('[Push] Checking Android push permissions...');
      const permissionStatus = await checkPushPermissions();
      console.log('[Push] Android permission status:', permissionStatus);
      
      if (!permissionStatus.allGranted) {
        console.log('[Push] Not all permissions granted, requesting...');
        // 只请求通知权限，电池优化在登录后再请求
        await requestAllPushPermissions({ showBatteryOptimization: false });
      }
    } catch (error) {
      console.warn('[Push] Error checking/requesting Android permissions:', error);
    }
  }
  
  initialized = true;
  bindBadgeClearListeners();
  bindResumePushListeners();

  // 确保原生侧（JCore/JPush）已收到“已同意”信号（并持久化），再做 SDK 初始化
  syncNativePrivacyConsent(true);
  clearPushBadge();

  // iOS 仅使用 JPush；Android 继续保留 FCM 兜底。
  await initJPush();
  if (!isIOSJPushOnlyMode()) {
    await initFCM();
  } else {
    console.log('[Push] iOS JPush-only mode active');
  }

  if (isUserLoggedIn()) {
    const bound = await tryUploadJPushRegId('initPush-end', { force: true, maxAttempts: 8 });
    if (!bound) {
      scheduleDeferredPushBind('initPush-end');
    }
  }
}

async function fetchJPushRegistrationIdWithRetry(JPush, stagePrefix, maxAttempts = 8) {
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const id = await requestJPushRegistrationId(
      JPush,
      `${stagePrefix}-attempt-${attempt}`,
      4000 + attempt * 500
    );
    if (id) {
      return id;
    }
    if (attempt < maxAttempts) {
      const delayMs = Math.min(2000 * attempt, 6000);
      console.log('[Push] JPush regId empty, scheduling retry', {
        stagePrefix,
        attempt,
        nextDelayMs: delayMs,
      });
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return null;
}

/**
 * 在用户登录成功后调用此方法，保存 token 到服务器
 * 应该在登录页面登录成功后调用
 */
export async function onUserLoggedIn() {
  // 未同意隐私协议时，不进行任何与推送相关的操作（包括 token 上报）
  if (!hasPrivacyAgreement()) {
    console.log('[Push] 用户未同意隐私协议，登录后也不进行推送 token 绑定');
    return;
  }

  logPushObject('[Push] onUserLoggedIn called', {
    jpushRegistrationId: maskToken(jpushRegistrationId),
    fcmToken: maskToken(fcmToken),
    native: Capacitor.isNativePlatform(),
  });
  logJPushProbe('onUserLoggedIn-start');

  const ensureLoginReady = async (attempt = 1) => {
    if (isUserLoggedIn()) return true;
    if (attempt >= 8) return false;
    await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
    return ensureLoginReady(attempt + 1);
  };

  const loginReady = await ensureLoginReady();
  logPushObject('[Push] onUserLoggedIn login state settled', { loginReady });

  if (!initialized && Capacitor.isNativePlatform()) {
    console.log('[Push] Push service not initialized, initializing now...');
    try {
      await initPush();
    } catch (e) {
      console.warn('[Push] Failed to initialize push service:', e);
    }
  }

  const bound = await ensurePushTokenBound('onUserLoggedIn');
  if (!bound) {
    scheduleDeferredPushBind('onUserLoggedIn');
  }

  const JPush = getJPushPlugin();
  if (JPush) {
    await runJPushDiagnostics(JPush, 'onUserLoggedIn-after-bind');
  }

  if (Capacitor.getPlatform() === 'android' && bound) {
    const { isXiaomiDevice } = await import('@/utils/xiaomiPermissionGuide');
    if (await isXiaomiDevice()) {
      console.log(
        '[Push] 小米机：划掉/杀后台收推送需开启「自启动」+「无限制省电」+ 通知权限；'
        + '服务端须发「通知消息」而非仅透传自定义消息。'
        + '设置入口：应用内 #/pushSettings 或登录后弹窗引导'
      );
    }
  }

  // 再尝试保存 FCM token（兜底）
  if (isIOSJPushOnlyMode()) {
    console.log('[Push] iOS JPush-only mode, skip FCM token handling after login');
    return;
  }

  if (fcmToken) {
    console.log('[Push] User logged in, saving FCM token...');
    await saveTokenToServer(fcmToken, 'fcm', { forceLoggedIn: loginReady });
  } else {
    console.log('[Push] No FCM token available yet, will save when received');
  }

  await setupAndroidPushAfterLogin();
}
